import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  LoadingBox, ErrorBox, PageShell, StatusPill, fmtDate, fmtBytes, useAdminApi,
} from "./_adminApi";

export default function BugReportDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const api = useAdminApi();
  const [item, setItem] = useState(null);
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState("");

  const load = () => {
    setErr(null);
    api.get(`admin/bug-reports/${id}/`)
      .then((r) => {
        setItem(r.data);
        setNotes(r.data?.admin_notes || "");
      })
      .catch(setErr);
  };
  useEffect(load, [id]);

  const updateStatus = async (status) => {
    setSaving(true);
    try {
      await api.patch(`admin/bug-reports/${id}/`, { status, admin_notes: notes });
      load();
    } catch (e) { setErr(e); } finally { setSaving(false); }
  };

  const saveNotes = async () => {
    setSaving(true);
    try {
      await api.patch(`admin/bug-reports/${id}/`, { admin_notes: notes });
      alert("Notes saved.");
    } catch (e) { setErr(e); } finally { setSaving(false); }
  };

  const del = async () => {
    if (!window.confirm("Delete this bug report and its video permanently?")) return;
    try {
      await api.del(`admin/bug-reports/${id}/`);
      navigate("/dashboard/admin/bug-reports");
    } catch (e) { setErr(e); }
  };

  if (err) return <PageShell title="Bug Report"><ErrorBox error={err} /></PageShell>;
  if (!item) return <PageShell title="Bug Report"><LoadingBox /></PageShell>;

  return (
    <PageShell
      breadcrumb={[
        { label: "Admin", to: "#" },
        { label: "App", to: "/dashboard/admin/app#bug-reports" },
        { label: `Bug #${item.id}` },
      ]}
      title={<span>Bug Report #{item.id}</span>}
      subtitle={item.title || "(no title)"}
      actions={
        <>
          <StatusPill status={item.status} />
          <button onClick={del} className="text-red-600 text-sm px-3 py-2 border border-red-300 rounded hover:bg-red-50">Delete</button>
        </>
      }
    >
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-black rounded-lg overflow-hidden">
            {item.video_url ? (
              <video src={item.video_url} controls className="w-full max-h-[60vh] bg-black" />
            ) : (
              <div className="text-gray-400 text-center py-20">Video unavailable.</div>
            )}
          </div>
          <div className="bg-white border rounded-lg p-4">
            <h4 className="font-semibold text-gray-700 mb-2">Description</h4>
            <p className="text-sm text-gray-800 whitespace-pre-line">{item.description || "(none provided)"}</p>
          </div>
          <div className="bg-white border rounded-lg p-4">
            <h4 className="font-semibold text-gray-700 mb-2">Admin notes (internal)</h4>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={5}
              className="w-full border rounded px-3 py-2 text-sm"
              placeholder="Investigation notes, workaround, etc."
            />
            <div className="flex justify-end mt-2">
              <button onClick={saveNotes} disabled={saving}
                className="px-4 py-2 rounded bg-primary-green text-white text-sm disabled:opacity-50">
                {saving ? "Saving…" : "Save notes"}
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white border rounded-lg p-4 text-sm">
            <h4 className="font-semibold text-gray-700 mb-3">Metadata</h4>
            <dl className="space-y-2">
              <Row label="Title" value={item.title} />
              <Row label="Reporter email" value={item.reporter_email || "(anonymous)"} />
              <Row label="Licence" value={item.licence_email || "—"} />
              <Row label="Store/Clinic" value={item.licence_store_name || "—"} />
              <Row label="App version" value={item.app_version || "—"} mono />
              <Row label="Duration" value={`${item.duration_seconds || 0} s`} />
              <Row label="Size" value={fmtBytes(item.size_bytes)} />
              <Row label="Received" value={fmtDate(item.created_at)} />
              <Row label="Resolved" value={item.resolved_at ? `${fmtDate(item.resolved_at)} by ${item.resolved_by_name || "—"}` : "—"} />
            </dl>
          </div>

          <div className="bg-white border rounded-lg p-4">
            <h4 className="font-semibold text-gray-700 mb-3">Update status</h4>
            <div className="flex flex-col gap-2">
              <button onClick={() => updateStatus("new")} disabled={saving || item.status === "new"}
                className="px-3 py-2 rounded border text-sm disabled:opacity-50 hover:bg-gray-50">Mark as New</button>
              <button onClick={() => updateStatus("in_progress")} disabled={saving || item.status === "in_progress"}
                className="px-3 py-2 rounded border text-sm bg-yellow-50 border-yellow-200 text-yellow-800 disabled:opacity-50">Mark In Progress</button>
              <button onClick={() => updateStatus("resolved")} disabled={saving || item.status === "resolved"}
                className="px-3 py-2 rounded border text-sm bg-green-50 border-green-200 text-green-800 disabled:opacity-50">Mark Resolved</button>
            </div>
          </div>

          <div className="bg-white border rounded-lg p-4 text-xs">
            <h4 className="font-semibold text-gray-700 mb-2">Device info</h4>
            <pre className="whitespace-pre-wrap break-words text-gray-600">
              {JSON.stringify(item.device_info || {}, null, 2)}
            </pre>
          </div>
        </div>
      </div>
    </PageShell>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-gray-500">{label}</dt>
      <dd className={`text-gray-800 text-right ${mono ? "font-mono text-xs" : ""}`}>{value}</dd>
    </div>
  );
}
